import React, { useEffect, useState, lazy, Suspense, forwardRef, useImperativeHandle } from "react";
import { useFormik } from "formik";
import "../../../styles.css";
import { useTranslation } from "react-i18next";
import { GetGeoFencing } from "../../../../src/components/Register/GetGeoFencing";
import { URLS } from "../../../API/API endpoints";
import axios from "axios";
import UnservicableCity from "../../../Popups/ShoplocationPopup";
import { shopLocationSchema } from "../../../components/validationSchema";
import { InputBox } from "../../../components/CustomWidgets/InputBox";

const MapContainer = lazy(() => import("../../../../src/components/locationPermission/googleMap"));

const SubStoreLocation = forwardRef(({ onChange, defaultData, isEdit }, ref) => {
    const { t, i18n } = useTranslation();

    const [selectedLocation, setSelectedLocation] = useState(null);
    const [address, setAddress] = useState({});
    const [selectedPolygon, setSelectedPolygon] = useState({});
    const [notServe, setNotServe] = useState(false);
    const [popup, setPopup] = useState(false);
    const [loading, setLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            address1: defaultData?.street2?.split(",")?.[0] || "",
            address2: defaultData?.street2?.split(",")?.[1] || "",
            street: defaultData?.street || "",
            zip: defaultData?.zip || "",
            state: defaultData?.state || "",
            lat: defaultData?.latitude || "",
            long: defaultData?.longitude || "",
        },
        validationSchema: shopLocationSchema,
        validateOnMount: true,
        onSubmit: () => { },
    });

    // ✅ Expose methods to parent
    useImperativeHandle(ref, () => ({
        getData: () => {
            if (!formik.isValid) return null;

            const street2 = `${formik.values.address1}${formik.values.address2 ? ", " + formik.values.address2 : ""
                }`;

            return {
                longitude: formik.values.long,
                latitude: formik.values.lat,
                street: formik.values.street,
                street2,
                zip: Number(formik.values.zip),
                state: formik.values.state,
                locality: address?.locality,
                area: selectedPolygon?.service_area || address?.locality,
                city: selectedPolygon?.level || address?.city,
                zone_id: selectedPolygon?.delivery_area_id || null,
            };
        },
        resetForm: formik.resetForm,
    }));

    // ✅ Pre-fill data when editing
    const [didPrefill, setDidPrefill] = useState(false);

    useEffect(() => {
        if (isEdit && defaultData && !didPrefill) {
            formik.setValues({
                address1: defaultData?.street2?.split(",")?.[0] || "",
                address2: defaultData?.street2?.split(",")?.[1] || "",
                street: defaultData?.street || "",
                zip: defaultData?.zip || "",
                state: defaultData?.state || "",
                lat: defaultData?.latitude || "",
                long: defaultData?.longitude || "",
            });

            setSelectedLocation({
                lat: parseFloat(defaultData?.latitude) || 0,
                lng: parseFloat(defaultData?.longitude) || 0,
            });

            formik.validateForm();
            setDidPrefill(true); // 🛑 Prevent rerunning
        }
    }, [isEdit, defaultData, didPrefill]);

    // ✅ Notify parent when data changes
    useEffect(() => {
        onChange?.(formik.isValid ? formik.values : null);
    }, [formik.values, formik.isValid]);

    const handleLocationChange = (loc) => {
        console.log("Location changed:", loc);

        setSelectedLocation(loc);
        formik.setFieldValue("lat", loc.lat);
        formik.setFieldValue("long", loc.lng);

        fetchAddressDetails(loc.lat, loc.lng);
    };

    const fetchAddressDetails = async (lat, lng) => {
        const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_G00GLE_API_KEY}`;

        try {
            const res = await fetch(url);
            const data = await res.json();
            if (data.status !== "OK") return;

            let locationData = {
                locality: "",
                city: "",
                state: "",
                postal_code: "",
            };

            formik.setFieldValue("lat", lat);
            formik.setFieldValue("long", lng);

            data?.results.forEach((result) => {
                result?.address_components?.forEach((comp) => {
                    const { types, long_name } = comp;
                    if (types.includes("locality")) locationData.locality = long_name;
                    if (types.includes("administrative_area_level_2"))
                        locationData.city = long_name;
                    if (types.includes("administrative_area_level_1"))
                        formik.setFieldValue("state", long_name);
                    if (types.includes("postal_code"))
                        formik.setFieldValue("zip", long_name);
                });
            });

            setAddress(locationData);
            GetPolygons(lat, lng);
        } catch (err) {
            console.error("Address error:", err);
        }
    };

    const GetPolygons = async (lat, lng) => {
        try {
            setLoading(true);
            const payload = { latitude: String(lat), longitude: String(lng) };
            const headers = { lang: i18n.language === "ar" ? "ar_001" : "en_US" };

            const res = await axios.post(URLS.NEW_GET_POLYGON, payload, { headers });

            if (res?.data?.result?.status_code === 200) {
                const geoValues = await GetGeoFencing(
                    { latitude: lat, longitude: lng },
                    res.data.result.response
                );

                if (geoValues?.isInPolygon) {
                    setSelectedPolygon(geoValues.deliveryArea);
                    setNotServe(false);
                } else {
                    setNotServe(true);
                    setSelectedPolygon({
                        delivery_area: "no-area",
                        delivery_area_id: "no-area",
                    });
                }
            } else {
                setNotServe(true);
            }
        } catch (error) {
            console.error("Polygon error:", error);
            setNotServe(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-2 bg-white custom-scrollbar">
            <h2 className="text-xl font-semibold mb-2">{t("set_location")}</h2>

            <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1 space-y-4">
                    <InputBox
                        label={t("address1")}
                        name="address1"
                        placeholder={t("enter_shopno")}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.address1}
                        error={formik.errors.address1}
                        touched={formik.touched.address1}
                    />

                    <InputBox
                        label={t("address2")}
                        name="address2"
                        placeholder={t("landmark")}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.address2}
                        error={formik.errors.address2}
                        touched={formik.touched.address2}
                    />

                    <InputBox
                        label={t("street")}
                        name="street"
                        placeholder={t("enter_street")}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.street}
                        error={formik.errors.street}
                        touched={formik.touched.street}
                    />
                </div>

                <div className="w-full md:w-[50%] h-[18rem] rounded-lg overflow-hidden shadow">
                    <Suspense fallback={<div>{t("loadingmap")}...</div>}>
                        <MapContainer
                            onLocationChange={handleLocationChange}
                            lat={(selectedLocation?.lat ?? 0).toString()}
                            lng={(selectedLocation?.lng ?? 0).toString()}
                        />
                    </Suspense>
                </div>
            </div>

            {popup && (
                <UnservicableCity
                    onClose={() => {
                        setPopup(false);
                    }}
                />
            )}
        </div>
    );
});

export default SubStoreLocation;




// import React, { useEffect, useState, lazy, Suspense, forwardRef, useImperativeHandle } from "react";
// import { useFormik } from "formik";
// import "../../../styles.css";
// import { useTranslation } from "react-i18next";
// import { GetGeoFencing } from "../../../../src/components/Register/GetGeoFencing";
// import { URLS } from "../../../API/API endpoints";
// import axios from "axios";
// import UnservicableCity from "../../../Popups/ShoplocationPopup";
// import { shopLocationSchema } from "../../../components/validationSchema";
// import { InputBox } from "../../../components/CustomWidgets/InputBox";

// const MapContainer = lazy(() => import("../../../../src/components/locationPermission/googleMap"));

// const SubStoreLocation = forwardRef(({ onChange, defaultData }, ref) => {
//     const { t, i18n } = useTranslation();

//     const [selectedLocation, setSelectedLocation] = useState(null);
//     const [address, setAddress] = useState({});
//     const [selectedPolygon, setSelectedPolygon] = useState({});
//     const [notServe, setNotServe] = useState(false);
//     const [popup, setPopup] = useState(false);
//     const [loading, setLoading] = useState(false);

//     const formik = useFormik({
//         initialValues: {
//             address1: defaultData?.street2?.split(",")?.[0] || "",
//             address2: defaultData?.street2?.split(",")?.[1] || "",
//             street: defaultData?.street || "",
//             zip: defaultData?.zip || "",
//             state: defaultData?.state || "",
//             lat: defaultData?.latitude || "",
//             long: defaultData?.longitude || "",
//         },
//         validationSchema: shopLocationSchema,
//         validateOnMount: true,
//         onSubmit: () => { /* no-op */ },
//     });

//     // Expose methods to parent using ref
//     useImperativeHandle(ref, () => ({
//         getData: () => {
//             if (!formik.isValid) return null;

//             return {
//                 longitude: formik.values.long,
//                 latitude: formik.values.lat,
//                 street: formik.values.street,
//                 street2: `${formik.values.address1}${formik.values.address2 ? ", " + formik.values.address2 : ""}`,
//                 zip: Number(formik.values.zip),
//                 state: formik.values.state,
//                 locality: address?.locality,
//                 area: selectedPolygon?.service_area || address?.locality,
//                 city: selectedPolygon?.level || address?.city,
//                 zone_id: selectedPolygon?.delivery_area_id || null,
//             };
//         },
//         resetForm: () => {
//             formik.resetForm();
//         }
//     }));

//     useEffect(() => {
//         onChange?.(formik.isValid ? formik.values : null);
//     }, [formik.values, formik.isValid]);

//     useEffect(() => {
//         navigator.geolocation.getCurrentPosition(
//             (pos) => {
//                 const coords = {
//                     lat: pos.coords.latitude,
//                     lng: pos.coords.longitude,
//                 };
//                 setSelectedLocation(coords);
//                 handleLocationChange(coords);
//             },
//             (error) => {
//                 console.error("Location error:", error);
//             }
//         );
//     }, []);

//     const handleLocationChange = (loc) => {
//         console.log("Location changed:", loc);

//         setSelectedLocation(loc);
//         fetchAddressDetails(loc.lat, loc.lng);
//     };

//     const fetchAddressDetails = async (lat, lng) => {
//         const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_G00GLE_API_KEY}`;

//         // const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_G00GLE_API_KEY}`;

//         try {
//             const res = await fetch(url);
//             const data = await res.json();

//             if (data.status !== "OK") return;

//             let locationData = {
//                 locality: "",
//                 city: "",
//                 state: "",
//                 postal_code: "",
//             };

//             formik.setFieldValue("lat", lat);
//             formik.setFieldValue("long", lng);

//             console.log("Geocode data:", data);


//             data?.results.forEach((result) => {
//                 result?.address_components?.forEach((comp) => {
//                     const { types, long_name } = comp;
//                     if (types.includes("locality")) locationData.locality = long_name;
//                     if (types.includes("administrative_area_level_2")) locationData.city = long_name;
//                     if (types.includes("administrative_area_level_1")) formik.setFieldValue("state", long_name);
//                     if (types.includes("postal_code")) formik.setFieldValue("zip", long_name);
//                 });
//             });

//             setAddress(locationData);
//             GetPolygons(lat, lng);
//         } catch (err) {
//             console.error("Address error:", err);
//         }
//     };

//     const GetPolygons = async (lat, lng) => {
//         try {
//             setLoading(true);

//             const payload = { latitude: String(lat), longitude: String(lng) };
//             const headers = {
//                 lang: i18n.language === "ar" ? "ar_001" : "en_US",
//             };

//             const res = await axios.post(URLS.NEW_GET_POLYGON, payload, headers);

//             if (res?.data?.result?.status_code === 200) {
//                 const geoValues = await GetGeoFencing({ latitude: lat, longitude: lng }, res.data.result.response);

//                 if (geoValues?.isInPolygon) {
//                     setSelectedPolygon(geoValues.deliveryArea);
//                     setNotServe(false);
//                 } else {
//                     setNotServe(true);
//                     setSelectedPolygon({
//                         delivery_area: "no-area",
//                         delivery_area_id: "no-area",
//                     });
//                 }
//             } else {
//                 setNotServe(true);
//             }
//         } catch (error) {
//             console.error("Polygon error:", error);
//             setNotServe(true);
//         } finally {
//             setLoading(false);
//         }
//     };

//     return (
//         <div className="p-2 bg-white custom-scrollbar">
//             <h2 className="text-xl font-semibold mb-6">{t("set_location")}</h2>

//             <div className="flex flex-col md:flex-row gap-6">
//                 {/* Form fields */}
//                 <div className="flex-1 space-y-4">
//                     <InputBox
//                         label={t("address1")}
//                         name="address1"
//                         placeholder={t("enter_shopno")}
//                         onChange={formik.handleChange}
//                         onBlur={formik.handleBlur}
//                         value={formik.values.address1}
//                         error={formik.errors.address1}
//                         touched={formik.touched.address1}
//                     />

//                     <InputBox
//                         label={t("address2")}
//                         name="address2"
//                         placeholder={t("landmark")}
//                         onChange={formik.handleChange}
//                         onBlur={formik.handleBlur}
//                         value={formik.values.address2}
//                         error={formik.errors.address2}
//                         touched={formik.touched.address2}
//                     />

//                     <InputBox
//                         label={t("street")}
//                         name="street"
//                         placeholder={t("enter_street")}
//                         onChange={formik.handleChange}
//                         onBlur={formik.handleBlur}
//                         value={formik.values.street}
//                         error={formik.errors.street}
//                         touched={formik.touched.street}
//                     />
//                 </div>

//                 {/* Map Section */}
//                 <div className="w-full md:w-[50%] h-[22rem] rounded-lg overflow-hidden shadow">
//                     <Suspense fallback={<div>{t("loadingmap")}...</div>}>
//                         <MapContainer
//                             onLocationChange={handleLocationChange}
//                             // lat={selectedLocation?.lat || 0}
//                             // lng={selectedLocation?.lng || 0}
//                             lat={(selectedLocation?.lat ?? 0).toString()}
//                             lng={(selectedLocation?.lng ?? 0).toString()}
//                         />
//                     </Suspense>
//                 </div>
//             </div>

//             {popup && (
//                 <UnservicableCity
//                     onClose={() => {
//                         setPopup(false);
//                     }}
//                 />
//             )}
//         </div>
//     );
// });

// export default SubStoreLocation;
